import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * "Dropdown with icons" variant option (snippets/variant-icon-dropdown.liquid).
 *
 * A listbox that mirrors the hidden native <select>. Choosing an option sets the select's
 * value and fires its `change` event, so <variant-picker> handles the variant switch exactly
 * as it does for the built-in dropdown (price, media, URL, add to cart). The variant picker
 * then re-renders this markup with the new selection.
 *
 * All listeners sit on the custom element itself, so they survive that re-render (morph
 * updates the children but keeps this element).
 */
class VariantIconDropdown extends HTMLElement {
  /** Index of the keyboard-highlighted option while the list is open */
  #activeIndex = -1;

  /** True between a press inside the dropdown and its click, so focus moving doesn't close the list */
  #pressInside = false;

  connectedCallback() {
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('focusout', this.#onFocusOut);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('focusout', this.#onFocusOut);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  get #button() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('.variant-icon-dropdown__button'));
  }

  get #list() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.variant-icon-dropdown__list'));
  }

  get #select() {
    return /** @type {HTMLSelectElement | null} */ (this.querySelector('select'));
  }

  get #options() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[role="option"]')));
  }

  get #isOpen() {
    return this.#list ? !this.#list.hidden : false;
  }

  #open() {
    const list = this.#list;
    const button = this.#button;
    if (!list || !button) return;

    list.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    const selectedIndex = this.#options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    this.#setActive(selectedIndex >= 0 ? selectedIndex : 0);
    list.focus({ preventScroll: true });
    this.#revealList();
    document.addEventListener('click', this.#onDocumentClick);
  }

  /**
   * If the open list runs past the bottom of the screen, scroll the page just enough to show
   * all of it (keeping the button in view too), so shoppers don't have to scroll to pick.
   */
  #revealList() {
    const list = this.#list;
    const button = this.#button;
    if (!list || !button) return;

    const margin = 16;
    const listBottom = list.getBoundingClientRect().bottom;
    const overflow = listBottom + margin - window.innerHeight;
    if (overflow <= 0) return;

    // Don't scroll so far that the button goes under the top of the screen / sticky header
    const headerHeight =
      Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const room = button.getBoundingClientRect().top - headerHeight - margin;
    const distance = Math.max(0, Math.min(overflow, room));
    if (distance === 0) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // The theme scrolls .page-wrapper on desktop and the document on mobile; the helper picks the right one
    scrollTo({ top: getScrollTop() + distance, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  #close({ focusButton = false } = {}) {
    const list = this.#list;
    if (list) {
      list.hidden = true;
      list.removeAttribute('aria-activedescendant');
    }
    this.#button?.setAttribute('aria-expanded', 'false');
    this.#options.forEach((option) => option.classList.remove('is-active'));
    this.#activeIndex = -1;
    document.removeEventListener('click', this.#onDocumentClick);
    if (focusButton) this.#button?.focus();
  }

  /** @param {number} index */
  #setActive(index) {
    const options = this.#options;
    if (!options.length) return;

    this.#activeIndex = Math.max(0, Math.min(index, options.length - 1));
    options.forEach((option, i) => option.classList.toggle('is-active', i === this.#activeIndex));

    const active = options[this.#activeIndex];
    const list = this.#list;
    if (!list) return;
    list.setAttribute('aria-activedescendant', active.id);

    // Keep the highlighted option visible inside the list without moving the page
    const top = active.offsetTop;
    const bottom = top + active.offsetHeight;
    if (top < list.scrollTop) {
      list.scrollTop = top;
    } else if (bottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = bottom - list.clientHeight;
    }
  }

  /** @param {number} index */
  #choose(index) {
    const select = this.#select;
    const option = this.#options[index];
    if (!select || !option) return;

    this.#close({ focusButton: true });

    if (option.getAttribute('aria-selected') === 'true') return;

    // Reflect the choice immediately; the variant picker re-render confirms it
    this.#options.forEach((item, i) => item.setAttribute('aria-selected', String(i === index)));
    const buttonName = this.#button?.querySelector('.variant-icon-dropdown__name');
    const buttonIcon = this.#button?.querySelector('.variant-icon-dropdown__icon-slot');
    const optionName = option.querySelector('.variant-icon-dropdown__name');
    const optionIcon = option.querySelector('.variant-icon-dropdown__icon-slot');
    if (buttonName && optionName) buttonName.textContent = optionName.firstChild?.textContent?.trim() ?? '';
    if (buttonIcon && optionIcon) buttonIcon.innerHTML = optionIcon.innerHTML;

    select.selectedIndex = index;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #onPointerDown = () => {
    // Safari/iOS don't focus buttons on tap, so pressing the button blurs the list with no
    // relatedTarget. Remember the press so #onFocusOut leaves closing to the click (toggle).
    this.#pressInside = true;
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    this.#pressInside = false;
    const target = /** @type {Element} */ (event.target);

    if (target.closest('.variant-icon-dropdown__button')) {
      this.#isOpen ? this.#close() : this.#open();
      return;
    }

    const option = target.closest('[role="option"]');
    if (option instanceof HTMLElement) {
      this.#choose(Number(option.dataset.index));
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    const onButton = event.target === this.#button;

    if (!this.#isOpen) {
      if (onButton && ['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        this.#open();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#setActive(this.#activeIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#setActive(this.#activeIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.#setActive(0);
        break;
      case 'End':
        event.preventDefault();
        this.#setActive(this.#options.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.#choose(this.#activeIndex);
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.#close({ focusButton: true });
        break;
      case 'Tab':
        this.#close();
        break;
    }
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (this.#pressInside) return;
    if (this.#isOpen && !this.contains(/** @type {Node | null} */ (event.relatedTarget))) {
      this.#close();
    }
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) this.#close();
  };
}

if (!customElements.get('variant-icon-dropdown')) {
  customElements.define('variant-icon-dropdown', VariantIconDropdown);
}
